import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import DataTable from "cxfcomponents/Tables/DataTable";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import SoftBox from "components/SoftBox";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import StatusBadge from "cxfcomponents/StatusBadge";
import Checkbox from "@mui/material/Checkbox";
import { useSlugFlow } from "hooks/useSlugFlow";
import { updateFlow } from "api/FlowDesignerApis";
import { useAuth0 } from "@auth0/auth0-react";
import defaultCredentialIcon from "assets/images/credential.svg";
import { getLocalDateTimeString, formatDateAndTime } from "helpers/common";
import { formatCredentialApplicationType } from "helpers/credentials";
import { useSelector } from "react-redux";
import UsersCell from "layouts/Flows/components/UsersCell";

export const AddCredentialDialog = ({
  openAddCredentialDialog,
  onClose,
  credentials = []
}) => {
  const handleClose = () => {
    onClose();
  };
  const [selectedCredentials, setSelectedCredentials] = useState([]);
  const { flow, handleFetchFlows } = useSlugFlow();
  const { user } = useAuth0();
  const { users } = useSelector((store) => store?.portal?.users);

  const handleAddCredentials = async () => {
    const newCredentials = selectedCredentials
      .map((selectedCredential) => {
        const foundCredential = credentials.find(
          (credential) => credential.id === selectedCredential.id
        );

        return foundCredential ? JSON.stringify(foundCredential) : null;
      })
      .filter((credential) => !!credential);

    const { id, updated_date_time, created_date_time, ...restFlow } = flow;

    const input = {
      ...restFlow,
      credentials: flow.credentials ? flow.credentials.concat(newCredentials) : newCredentials
    };

    await updateFlow(user?.stripeId, flow.id, input, true);
    handleFetchFlows();
    onClose();
  };

  const handleFilterCredentials = (credential) => {
    if (flow.credentials?.find((flowCredential) => flowCredential.includes(credential.id))) {
      return false;
    }

    return true;
  };

  const handleCredentialAddition = (credential) => {
    setSelectedCredentials((prev) => {
      if (prev.find((prevCredential) => prevCredential.id === credential.id)) {
        return prev.filter((prevCredential) => prevCredential.id !== credential.id);
      }

      return prev.concat(credential);
    });
  };

  const determineOwnerSharedUsers = (share_user, owner_id) => {
    if (share_user && users) {
      const share_user_ids = share_user?.map((sUser) => sUser?.user_id);
      const sharedUsersAndOwnerData = users?.filter(
        (user) => share_user_ids?.includes(user.auth0_id) || user.auth0_id === owner_id
      );

      return sharedUsersAndOwnerData;
    }
  };

  const tableData = useMemo(() => {
    return {
      columns: [
        {
          Header: "selected",
          accessor: "id",
          canGroupBy: false,
          disableSortBy: true,
          disableGlobalFilter: true,
          disableResizing: true,
          Cell: ({ row }) => (
            <SoftBox sx={{ maxWidth: "48px" }}>
              <Checkbox
                checked={
                  !!selectedCredentials.find((credential) => credential.id === row.original.id)
                }
                onChange={() => {
                  handleCredentialAddition({
                    id: row.original.id,
                    name: row.original.name,
                    type: "Credential"
                  });
                }}
              />
            </SoftBox>
          )
        },
        {
          Header: "developer_logo",
          accessor: "developer_logo",
          show: false,
          canGroupBy: false,
          disableSortBy: true
        },
        {
          Header: "icon",
          accessor: "icon",
          canGroupBy: false,
          disableSortBy: true,
          Cell: ({ value }) => (
            <SoftBox width="60px" height="60px">
              {value}
            </SoftBox>
          )
        },
        {
          Header: "name",
          accessor: "name",
          width: 250,
          canGroupBy: false,
          Cell: ({ row, value }) => (
            <SoftTypography fontSize="14px" fontWeight="bold">
              <Link
                style={{ color: "#2D3748" }}
                className="white-to-dark"
                to={`/connector/${row.original.id}`}
              >
                {value}
              </Link>
            </SoftTypography>
          )
        },
        {
          Header: "status",
          accessor: "status",
          canGroupBy: false,
          Cell: ({ value }) => {
            return <StatusBadge status={value?.toLowerCase() ?? "active"} />;
          }
        },
        {
          Header: "Users",
          accessor: "users",
          disableSortBy: true,
          Cell: ({ value }) => <UsersCell users={value} />
        },
        {
          Header: "Created",
          accessor: "created",
          disableSortBy: false,
          disableGlobalFilter: true,
          Cell: ({ value }) => (
            <SoftTypography fontSize="12px">
              {value}
            </SoftTypography>
          )
        },
        {
          Header: "Last updated",
          accessor: "last_updated",
          disableSortBy: false,
          Cell: ({ value }) => (
            <SoftTypography fontSize="12px">
              {value}
            </SoftTypography>
          )
        }
      ].map((column) => ({ ...column, disableResize: true })),
      rows: credentials.filter(handleFilterCredentials).map((credential) => {
        return {
          id: credential.id,
          name: credential.application.name,
          type: credential.application.applicationType
            ? formatCredentialApplicationType(credential.application.applicationType)
            : "",
          status: credential.status,
          icon: <SoftBox component="img" width="100%" src={defaultCredentialIcon} />,
          created: credential.create_date_time
            ? formatDateAndTime(getLocalDateTimeString(credential.create_date_time))
            : "",
          last_updated: credential.updated_date_time
            ? formatDateAndTime(getLocalDateTimeString(credential.updated_date_time))
            : "",
          users: determineOwnerSharedUsers(credential.share_user, credential.owner_id) || []
        };
      })
    };
  }, [selectedCredentials, flow.connectors]);

  useEffect(() => {
    setSelectedCredentials([]);
  }, [openAddCredentialDialog]);

  return (
    <Dialog fullWidth maxWidth="md" open={openAddCredentialDialog} onClose={onClose}>
      <DialogContent>
        <DialogTitle>Add Credential</DialogTitle>
        <SoftBox width={{ xs: "100%" }} maxWidth={{ xl: "100px" }}></SoftBox>
        <DataTable
          table={tableData}
          entriesPerPage={{ defaultValue: 25 }}
          hiddenTopButtons
          tableView={"list"}
        />
      </DialogContent>
      <DialogActions>
        <SoftButton variant="outlined" color="info" onClick={handleClose}>
          Close
        </SoftButton>
        <SoftButton color="info" onClick={handleAddCredentials}>
          Add
        </SoftButton>
      </DialogActions>
    </Dialog>
  );
};
